import type { IconBrandName, IconName } from '@mdvip/shared/components/blocks/Icon';
import { Icon } from '@mdvip/shared/components/blocks/Icon';
import { buttonVariants } from '@mdvip/shared/components/ui/button';
import type * as React from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

interface ResourceItem {
  to: string;
  icon: IconName;
  labelKey: string;
  blurbKey: string;
  external?: boolean;
}

const WELLBEING_ITEMS = [
  {
    to: '/resources/nutrition',
    icon: 'utensils',
    labelKey: 'pages.resources.wellbeing.nutrition.label',
    blurbKey: 'pages.resources.wellbeing.nutrition.blurb',
  },
  {
    to: '/resources/wellness',
    icon: 'bookOpen',
    labelKey: 'pages.resources.wellbeing.wellness.label',
    blurbKey: 'pages.resources.wellbeing.wellness.blurb',
  },
  {
    to: '/resources/fitness',
    icon: 'dumbbell',
    labelKey: 'pages.resources.wellbeing.fitness.label',
    blurbKey: 'pages.resources.wellbeing.fitness.blurb',
  },
] as const satisfies readonly ResourceItem[];

const COMMUNITY_ITEMS = [
  {
    to: 'https://www.mdvip.com/blog',
    icon: 'newspaper',
    labelKey: 'pages.resources.community.livingWellBlog.label',
    blurbKey: 'pages.resources.community.livingWellBlog.blurb',
    external: true,
  },
  {
    to: 'https://www.mdvip.com/patients/resources',
    icon: 'mail',
    labelKey: 'pages.resources.community.mdvipNewsletter.label',
    blurbKey: 'pages.resources.community.mdvipNewsletter.blurb',
    external: true,
  },
  {
    to: 'https://www.mdvip.com/patients/member-stories',
    icon: 'users',
    labelKey: 'pages.resources.community.memberStories.label',
    blurbKey: 'pages.resources.community.memberStories.blurb',
    external: true,
  },
  {
    to: 'https://www.yelp.com/writeareview/biz/DU1InbSJhAIHNiWu7RcCDA',
    icon: 'pen',
    labelKey: 'pages.resources.community.tellYourStory.label',
    blurbKey: 'pages.resources.community.tellYourStory.blurb',
    external: true,
  },
  {
    to: 'https://www.mdvip.com/about-mdvip/foundation',
    icon: 'landmark',
    labelKey: 'pages.resources.community.mdvipFoundation.label',
    blurbKey: 'pages.resources.community.mdvipFoundation.blurb',
    external: true,
  },
] as const satisfies readonly ResourceItem[];

/* `ResourceItem`'s own `labelKey`/`blurbKey: string` would widen the two const arrays' literal
   key types back to plain `string`, which breaks `t()`'s key-checking — same fix as
   `Navigation.tsx`'s `AccountRowSpec`. */
type ResourceItemSpec = Omit<ResourceItem, 'labelKey' | 'blurbKey'> & {
  labelKey: (typeof WELLBEING_ITEMS)[number]['labelKey'] | (typeof COMMUNITY_ITEMS)[number]['labelKey'];
  blurbKey: (typeof WELLBEING_ITEMS)[number]['blurbKey'] | (typeof COMMUNITY_ITEMS)[number]['blurbKey'];
};

// Matches the focus-ring recipe `Navigation` already defines locally for its own
// hand-styled links — small enough that a shared export isn't worth it.
const FOCUS_RING =
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background';

const TILE_SHELL_CLASS = 'flex flex-col gap-4 rounded-xl border bg-card p-6 shadow-xs';
const TILE_CLASS = `group ${TILE_SHELL_CLASS} transition-[transform,box-shadow,border-color] duration-200 ease-out hover:border-hairline-brand hover:shadow-md motion-safe:hover:-translate-y-0.5 ${FOCUS_RING}`;
const TILE_BADGE_CLASS = 'flex size-11 shrink-0 items-center justify-center rounded-lg bg-tint-brand text-primary';

function ResourceTile({ item }: { item: ResourceItemSpec }) {
  const { t } = useTranslation('members');

  const content = (
    <span className="flex flex-col gap-2">
      <span className="flex items-center gap-3 text-base font-semibold text-foreground">
        <span className={TILE_BADGE_CLASS}>
          <Icon name={item.icon} size="xl" />
        </span>
        {t(item.labelKey)}
        {item.external && <Icon name="externalLink" size="xs" className="text-muted-foreground" />}
      </span>
      <span className="text-sm leading-relaxed text-pretty text-muted-foreground">{t(item.blurbKey)}</span>
    </span>
  );

  return item.external ? (
    <a href={item.to} target="_blank" rel="noopener noreferrer" className={TILE_CLASS}>
      {content}
    </a>
  ) : (
    <Link to={item.to} className={TILE_CLASS}>
      {content}
    </Link>
  );
}

// Channel names are brand marks, so they stay untranslated — only the surrounding
// "MDVIP on {channel}" label is a string.
const SOCIAL_CHANNELS = [
  { name: 'X', icon: 'xTwitter', href: 'https://x.com/MDVIP' },
  { name: 'Facebook', icon: 'facebook', href: 'https://www.facebook.com/MDVIP' },
  { name: 'YouTube', icon: 'youtube', href: 'https://www.youtube.com/mdvip' },
  { name: 'LinkedIn', icon: 'linkedin', href: 'https://www.linkedin.com/company/mdvip' },
  { name: 'Instagram', icon: 'instagram', href: 'https://www.instagram.com/mymdvip/' },
] as const satisfies readonly { name: string; icon: IconBrandName; href: string }[];

function FollowMDVIPTile() {
  const { t } = useTranslation('members');

  return (
    <div className={TILE_SHELL_CLASS}>
      <span className="flex items-center gap-3 text-base font-semibold text-foreground">
        <span className={TILE_BADGE_CLASS}>
          <Icon name="shareNodes" size="xl" />
        </span>
        {t('pages.resources.followMdvip.label')}
      </span>
      <span className="text-sm leading-relaxed text-pretty text-muted-foreground">
        {t('pages.resources.followMdvip.blurb')}
      </span>
      <nav aria-label={t('pages.resources.followMdvip.navLabel')} className="flex flex-wrap justify-center gap-3 pt-1">
        {SOCIAL_CHANNELS.map((channel) => (
          <a
            key={channel.name}
            href={channel.href}
            target="_blank"
            rel="noopener noreferrer"
            aria-label={t('pages.resources.followMdvip.channelLabel', { channel: channel.name })}
            // `buttonVariants` rather than `Button`: these are links, and Base UI's Button asserts
            // native button semantics over an anchor (`role="button"`), which loses that.
            // `shadow-none` because the design's social circles are flat; the variant's `shadow-xs`
            // is the only visible class it adds that the design doesn't have.
            className={buttonVariants({ size: 'icon-lg', className: 'size-11 shadow-none' })}
          >
            <Icon name={channel.icon} size="lg" />
          </a>
        ))}
      </nav>
    </div>
  );
}

function ResourceSection({
  headingKey,
  items,
  children,
}: {
  headingKey: 'pages.resources.wellbeingHeading' | 'pages.resources.communityHeading';
  items: readonly ResourceItemSpec[];
  children?: React.ReactNode;
}) {
  const { t } = useTranslation('members');

  return (
    <section className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4 border-b-2 border-border pb-4">
        <h2 className="text-h3 font-semibold text-foreground">{t(headingKey)}</h2>
        <span className="text-xs font-medium text-muted-foreground">
          {t('pages.resources.resourceCount', { count: items.length })}
        </span>
      </div>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {items.map((item) => (
          <ResourceTile key={item.labelKey} item={item} />
        ))}
        {children}
      </div>
    </section>
  );
}

export function ResourcesPage() {
  const { t } = useTranslation('members');

  return (
    <>
      <div className="mb-8">
        <h1 className="text-h1 font-semibold">{t('pages.resources.title')}</h1>
        <p className="text-muted-foreground mt-1">{t('pages.resources.subtitle')}</p>
      </div>
      <div className="flex flex-col gap-10">
        <ResourceSection headingKey="pages.resources.wellbeingHeading" items={WELLBEING_ITEMS} />
        <ResourceSection headingKey="pages.resources.communityHeading" items={COMMUNITY_ITEMS}>
          <FollowMDVIPTile />
        </ResourceSection>
      </div>
    </>
  );
}
